import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import Lazy from '@prisjakt/ui-lazy';
import { ScrollElement } from 'src/components/scroll';
import Recommendations from 'src/components/recommendations';
import { PageSection } from 'src/components/page';
import { NoStockStatus } from '../../../no-stock-status';
import PriceList from '../../../price-list';
import ReadableProductInformation from '../../../readable-product-information';
import { filterOffers } from 'src/components/price-list/filtering';
import { getAllOffersAsArray } from 'src/components/price-list/sorting';

import { AppState } from 'src/reducers';

import { trackComparisonLinkClick } from 'src/areas/product-compare/tracking';

import { getSimilarProductsCompareLink } from '../../utils';

import { PRODUCT_CONTEXT_ID } from 'src/areas/product/constants';

import { CurrentCampaign } from 'src/areas/product/types';
import { ModelType, ModelTypeEnum } from 'src/components/recommendations/types';
import { IProductTabMainProps } from 'src/areas/product/components/product-tabs/types';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';

import { StyledPageSection } from './styles';
import { PriceListContext } from '../../../price-list/price-list-context';
import { TrendingProducts } from '../../../trending-products';
import { UxLiteQuestions } from 'src/components/ux-feedback';
import { useLocalStorage } from 'src/hooks/use-localstorage';
import { useVariantValues } from '../../../product-variants/hooks/use-variant-values';

const STATUS_IN_STOCK = 'in_stock';

const ProductTabMain: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IProductTabMainProps>
>> = props => {
  const { product, recommendations, currentCampaign, trendingProducts } = props;
  const [searchUXFeedbackTime] = useLocalStorage('pj:searchUXFeedbackTime', 0);
  const { t, linkTo } = useContext(AppContext);
  const {
    getActiveFilters,
    isAnyFilterToggledOnByUser,
  } = useOfferListFilterContext();

  const condition = useSelector(
    (state: AppState) => state.areas.product.condition
  );

  const similarProducts = recommendations?.find(
    item => item?.model === ModelTypeEnum.SIMILAR_PRODUCTS
  );
  const handleCompareLinkClick = () => {
    if (!similarProducts?.recommendations?.length) return null;

    return trackComparisonLinkClick(
      similarProducts.recommendations.length + (product ? 1 : 0),
      'similarProducts'
    );
  };

  const similarProductsCompareLink = getSimilarProductsCompareLink(
    similarProducts,
    product,
    linkTo
  );

  //  NoStockStatus prompt should only be shown for 'new' products.
  //  That's because the prompt has an incentive to create a stock alert,
  //  and the stock alert function doesn't send notifications for 'used' products that is out of stock.
  //  Don't show it either if there is any filter activated, because it has it's own empty offer list message.
  const showNoStockStatusMessage =
    product.stockStatus !== STATUS_IN_STOCK &&
    condition === 'new' &&
    !isAnyFilterToggledOnByUser();

  const getRecommendationsLink = (modelType: ModelType) => {
    if (
      modelType === ModelTypeEnum.SIMILAR_PRODUCTS &&
      similarProductsCompareLink
    )
      return {
        title: t('product', 'product-compare.compare'),
        url: similarProductsCompareLink,
        onClick: handleCompareLinkClick,
      };

    if (modelType === ModelTypeEnum.PERSONALIZED)
      return {
        title: t('account', 'menu.settings'),
        url: '/personalization',
      };

    return null;
  };

  const filteredOffers = filterOffers(
    getAllOffersAsArray(product.prices),
    getActiveFilters()
  );
  const variantsValuesInOffers = useVariantValues(filteredOffers);

  return (
    <>
      <StyledPageSection noPad="top" recessed>
        {product.prices && (
          <ScrollElement name="Pricelist" data-test="MainTabContent">
            <PriceListContext.Provider
              value={{
                product,
                ourChoice: product.prices.ourChoice,
                variantValues: variantsValuesInOffers,
              }}
            >
              <PriceList
                productId={product.id}
                productName={product.name}
                media={product.media}
                brand={product.brand}
                priceList={product.prices}
                filteredOffers={filteredOffers}
                conditions={product.conditions}
                campaign={currentCampaign as CurrentCampaign}
              />
            </PriceListContext.Provider>
          </ScrollElement>
        )}

        {showNoStockStatusMessage && (
          <NoStockStatus category={product.category} product={product} />
        )}

        <TrendingProducts trendingProducts={trendingProducts} />

        {recommendations?.length ? (
          <Lazy>
            {recommendations.map(item => {
              if (!item.recommendations?.length) return null;
              const model = item.model as ModelType;
              const link = getRecommendationsLink(model);
              return (
                <Recommendations
                  key={`recommendations-model-${model}`}
                  link={link}
                  recommendations={item.recommendations}
                  type={model}
                  contextId={PRODUCT_CONTEXT_ID}
                  background
                />
              );
            })}
          </Lazy>
        ) : null}

        {searchUXFeedbackTime === 0 && <UxLiteQuestions name="product" />}
      </StyledPageSection>
      <PageSection noBorder>
        <ReadableProductInformation product={product} />
      </PageSection>
    </>
  );
};

export default ProductTabMain;
